import React, { FC, Fragment, useState, SyntheticEvent, useEffect, MouseEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Order, OrderStatus, OrderRejectStatus, SampleType, OrderType } from 'models/order';
import {
    AccordionDetails,
    Box,
    Grid,
    Tooltip,
    Typography,
    styled,
    Button,
    Chip,
    Accordion,
    useMediaQuery,
    Theme,
} from '@mui/material';
import OrderDetailsTopBar from 'components/order-details/main-order-view/order-details-top-bar.component';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import OrderInfoSection from 'components/order-details/main-order-view/order-info-section/order-info-section.componet';
import PriceSpecificationSectionWithHighlighting from 'components/order-details/main-order-view/price-specification-section/price-specification-section-with-highlighting.componet';
import RestorationsSection from 'components/order-details/main-order-view/restorations-section/restorations-section.component';
import DeliverySection from 'components/order-details/main-order-view/delivery-section/delivery-section.componet';
import LabSection from 'components/order-details/main-order-view/lab-section/lab-section.componet';
import SamplePackageSection from 'components/order-details/main-order-view/sample-package-section/sample-package-section.componet';
import FilesImagesSection from 'components/order-details/main-order-view/files-images-section/files-images-section.componet';
import AttachedScansSection from 'components/order-details/main-order-view/attached-scans-section/attached-scans-section.component';
import InvoiceActions from 'components/order-details/main-order-view/invoice-actions/invoice-actions.componet';
import DocumentPrintActions from 'components/order-details/main-order-view/document-print-actions/document-print-actions.componet';
import WorkflowActions from 'components/shared/workflow-actions/workflow-actions.component';
import OrderLinks from 'components/order-details/main-order-view/order-links/order-links.componet';
import SubcontractSelection from 'components/order-details/main-order-view/subcontract-selection/subcontract-selection.component';
import OrderStatusLog from 'components/order-details/main-order-view/order-status-log/order-status-log.componet';
import OrderVersionSelect from 'components/order-details/main-order-view/order-version-select/order-version-select.componet';
import OrderComments from 'components/order-details/main-order-view/order-comments/order-comments.componet';
import RateOrderSection from 'components/order-details/main-order-view/rate-order-section/rate-order-section.component';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import Deviations from 'components/order-details/main-order-view/deviation-section/deviations.componenet';
import { User, DentistNotes } from 'models/user';
import { permissions } from 'utils/permission';
import DeviationForDentist from 'components/order-details/main-order-view/deviation-section/deviation-for-dentist';
import { formatCurrency } from 'utils/format';
import { useNavigate } from 'react-router-dom';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import ChatMessages from '../chat-message/chat-message.component';
import { useMutation } from '@apollo/client';
import { markOrderNotificationsAsReadTemplate } from 'apollo/templates/notification';
import ManageExternalTechnicians from 'components/shared/manage-external-users/manage-external-technicians.component';
import AddIcon from '@mui/icons-material/Add';
import { OrderTag } from 'models/tags';
import AssignTagInOrderDialog from 'components/shared/assign-tag-in-order-dialog/assign-tag-in-order-dialog.component';
import { assignOrderTagTemplate } from 'apollo/templates/order-tags';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ManageParticipantsDialog from 'components/orders/order-details/manage-participants/manage-participants-dialog.component';
import { CurrencyContext } from 'context/currency';
import TransportActions from 'components/order-details/main-order-view/transport-actions/transport-actions.componet';
import OrderFormsSection from 'components/order-details/main-order-view/order-forms/order-forms-section.component';
import OrderDetailsTopMessages from 'components/order-details/main-order-view/order-details-top-messages.component';
import ParticipantsList from 'components/shared/manage-participants/participants-list.component';
import { ClinicNote, Organization } from 'models/organization';
import { isCustomer, isSupplier } from 'utils/client-settings';
import { isSupplierOrder } from 'utils/orders';
import { ScannerCase } from 'models/scanner-case';
import StickyInternalNotes from 'components/order-details/main-order-view/sticky-internal-notes/sticky-internal-notes.component';
import { restorationPriceRows } from 'utils/restoration-price-rows';

const defaultAccordionStates: { [key: string]: boolean } = {
    ORDER: true,
    COMMENTS: true,
    INTERNAL_NOTES: true,
    CONSTRUCTIONS: true,
    ATTACHED_SCANS: true,
    FILES_AND_IMAGES: true,
    ORDER_FORMS: true,
    PRICE_SPECIFICATION: true,
    DELIVERY: true,
    LAB: true,
    SAMPLE_AND_PACKAGE_CONTENT: true,
    RATING: true,
    DEVIATIONS: true,
    INVOICING: true,
    DOCUMENTS_AND_PRINT: true,
    TRANSPORT: true,
    MANAGE_EXTERNAL_USERS: true,
    LINKS: true,
    WORKFLOW: true,
    STATUS_LOG: true,
    PARTICIPANTS: true,
};

const collapsedAccordionStates: { [key: string]: boolean } = {
    ...Object.fromEntries(Object.keys(defaultAccordionStates).map((key) => [key, false])),
    WORKFLOW: true,
};

type MainOrderViewProps = {
    order: Order;
    selectedVersion: number;
    currentUser: User;
    handleVersionChange: (version: number) => void;
    maxVersion: number;
    onHandleDeliveryNoteCreation: () => void;
};
const MainOrderView: FC<MainOrderViewProps> = ({
    order,
    selectedVersion,
    currentUser,
    handleVersionChange,
    maxVersion,
    onHandleDeliveryNoteCreation,
}: MainOrderViewProps) => {
    const { t: translate } = useTranslation([
        'orders',
        'deviations',
        'workflow',
        'common',
        'lab-settings',
        'order-forms',
    ]);
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [accordionStates, setAccordionStates] = useState<{ [key: string]: boolean }>(() => {
        if (isMobile) return collapsedAccordionStates;
        const stored = JSON.parse(localStorage.getItem('accordionStates') || '{}');
        if (stored && Object.keys(stored).length > 0) return stored;
        return defaultAccordionStates;
    });
    const [versionCheck, setVersionCheck] = useState(false);
    const [dentistNote, setDentistNote] = useState<DentistNotes>();
    const [clinicNote, setClinicNote] = useState<ClinicNote>();
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [orderTags, setOrderTags] = useState<OrderTag[]>();
    const [rerender, setRerender] = useState(false);
    const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
    const { currency } = useContext(CurrencyContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );

    const isOrderFormEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ORDER_FORM_AI,
    );

    const isScannerCasesEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SCAN_CASES,
    );

    const isSimpleOrder = order.clinicOrderVersion === CONSTANTS.SETTINGS_VALUES.SIMPLE;
    const userIsCustomer = isCustomer(currentOrganization._id, order.customer._id);

    const canShowAttachScannerCaseButton = isScannerCasesEnabled && !(userIsCustomer && isSimpleOrder);

    useEffect(() => {
        setVersionCheck(selectedVersion > 0 ? selectedVersion === maxVersion : order.version === maxVersion);
    }, [selectedVersion, maxVersion]);

    const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
        background: '#f1f1f1',
        borderBottom: '1px #eee solid',
    }));

    const [markOrderNotificationsAsRead] = useMutation(markOrderNotificationsAsReadTemplate, {
        onCompleted: () => {
            window.top?.postMessage(
                {
                    eventType: 'refresh-notifications',
                },
                '*',
            );
        },
    });

    const [assignOrderTag] = useMutation(assignOrderTagTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    useEffect(() => {
        getDentistNote();
        getClinicNote();
        markOrderNotificationsAsRead({
            variables: {
                orderId: order._id,
            },
        });
    }, []);

    useEffect(() => {
        setOrderTags(order.orderTags);
    }, [order]);

    const handleAccordionChange = (accordionName: string) => (_: SyntheticEvent, isExpanded: boolean) => {
        setAccordionStates((prevState: { [key: string]: boolean }) => {
            const value = {
                ...prevState,
                [accordionName]: isExpanded,
            };
            localStorage.setItem('accordionStates', JSON.stringify(value));
            return value;
        });
    };

    // DD-3484: `parentOrder` resolves only for the lab that subcontracted this order. That
    // lab cannot edit the sub-order through the order flows — they know nothing of the
    // parent link or of what was selected to travel (the backend refuses it too).
    const isOwnSubOrder = Boolean(order.parentOrder);
    const canUseEditFlows = versionCheck && !isOwnSubOrder;

    const redirectToModifyOrder = (isResend: boolean) => {
        if (isCustomer(currentOrganization._id, order.customer._id) && order.isCreatedByLab) {
            return;
        }
        if (isOwnSubOrder) {
            return;
        }
        if (order.isCreatedByLab || isSupplierOrder(order)) {
            const scanCase = order.scannerCase as ScannerCase;
            const resendString = isResend ? 'isResend=true' : '';
            const caseIdStr = scanCase?._id ? `caseId=${scanCase._id}` : '';
            const queryString = resendString && caseIdStr ? `${resendString}&${caseIdStr}` : resendString || caseIdStr;
            if (isSupplierOrder(order)) {
                navigate(`/orders/update/supplier/${order._id}?${queryString}`);
            } else {
                navigate(`/orders/modify-lab/${order._id}?${queryString}`);
            }
        } else if (order.type === OrderType.SIMPLE_TYPE) {
            navigate(`/orders/modify-simple/${order._id}${isResend ? '?isResend=true' : ''}`);
        } else {
            navigate(`/orders/modify-action/${order._id}?isResend=${isResend}`);
        }
    };

    const getDentistNote = () => {
        if (order.dentist?.dentistNotes) {
            const note = order.dentist.dentistNotes.find(
                (note) => note.lab === order.supplier?._id && note.clinic === order.customer?._id,
            );
            if (note) {
                setDentistNote(note);
            }
        }
    };

    const getClinicNote = () => {
        if (order.deliveryDetails?.destination?.clinicInternalNotes) {
            const clinicNote = order.deliveryDetails.destination.clinicInternalNotes.find(
                (note) => note.lab?.toString() === order.supplier?._id.toString(),
            );
            if (clinicNote) {
                setClinicNote(clinicNote);
            }
        }
    };

    const handleAddTagClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsAddEditOpen(true);
    };

    const handleEditTagClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setRerender(true);
        setIsAddEditOpen(true);
    };

    const handleAddUpdateTag = async (tags: OrderTag[]) => {
        const ids = tags.map((tag) => tag._id);
        const { data } = await assignOrderTag({
            variables: {
                orderId: order?._id,
                tagIds: ids,
            },
        });
        const assignedTags = data?.assignOrderTag?.orderTags ?? tags;
        setOrderTags(assignedTags);
        handleCloseAddEdit();
    };

    const handleCloseAddEdit = () => {
        setIsAddEditOpen(false);
        setRerender(false);
    };

    const handleAddParticipantsClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setParticipantsDialogOpen(true);
    };

    const handleCloseParticipantsDialog = () => {
        setParticipantsDialogOpen(false);
    };

    return (
        <Fragment>
            <Box data-test="main-order-view.top-messages">
                <OrderDetailsTopMessages order={order} />
            </Box>
            <Box
                mt={-2}
                mb={1}
                py={2}
                sx={{
                    position: 'sticky',
                    top: '60px',
                    zIndex: 1000,
                    backgroundColor: '#fdfbfd',
                    borderBottom: '1px solid #bbb',
                }}
            >
                <OrderDetailsTopBar
                    currency={currency}
                    order={order}
                    onHandleDeliveryNoteCreation={onHandleDeliveryNoteCreation}
                />
            </Box>

            {/* Version picker on Mobile */}
            {!permissions.isExternalTechnician(currentUser.role) ? (
                <Box mb={2} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <OrderVersionSelect
                        selectedVersion={selectedVersion}
                        order={order}
                        onVersionSelect={handleVersionChange}
                    />
                </Box>
            ) : undefined}

            <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                    <Accordion onChange={handleAccordionChange('ORDER')} expanded={accordionStates['ORDER']}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-test="main-order-view.order-accordion">
                            <Box display="flex" flexGrow={1} alignItems="center">
                                <Box display="flex" flexGrow={1} alignItems="center">
                                    <Typography variant="h4">
                                        {translate('orders:ORDER')}: {order.orderId}
                                    </Typography>
                                    <Box ml={1}>
                                        {order.deliveryDetails.sampleType === SampleType.DIGITAL ? (
                                            <Tooltip title={<span>{translate('orders:DIGITAL')}</span>}>
                                                <AttachFileIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                            </Tooltip>
                                        ) : undefined}
                                        {order.deliveryDetails.sampleType === SampleType.PHYSICAL ? (
                                            <Tooltip title={<span>{translate('orders:PHYSICAL')}</span>}>
                                                <Inventory2OutlinedIcon
                                                    sx={{ color: 'text.secondary' }}
                                                    fontSize="small"
                                                />
                                            </Tooltip>
                                        ) : undefined}
                                        {order.deliveryDetails.sampleType === SampleType.NO_SAMPLE ? (
                                            <Tooltip title={<span>{translate('orders:NO_SAMPLE')}</span>}>
                                                <CircleOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                            </Tooltip>
                                        ) : undefined}
                                    </Box>
                                    {isSupplier(currentOrganization._id, order.supplier._id) &&
                                    !order.isImportedOrder ? (
                                        <Box ml={1}>
                                            {orderTags?.length ? (
                                                <>
                                                    {orderTags.map((tag) => (
                                                        <Chip
                                                            key={tag._id}
                                                            size="small"
                                                            clickable
                                                            label={tag.name}
                                                            sx={{
                                                                minWidth: 100,
                                                                backgroundColor: tag.color,
                                                                color: '#ffffff',
                                                                marginRight: 1,
                                                                '&:hover': {
                                                                    backgroundColor: tag.color,
                                                                    opacity: 0.7,
                                                                },
                                                            }}
                                                            onClick={handleEditTagClick}
                                                            data-test="main-order-view.edit-tag-chip"
                                                        />
                                                    ))}
                                                </>
                                            ) : (
                                                <Button
                                                    onClick={handleAddTagClick}
                                                    startIcon={<AddIcon />}
                                                    size="small"
                                                    sx={{ margin: 0, display: { xs: 'none', sm: 'inline-flex' } }}
                                                    data-test="main-order-view.add-tag-btn"
                                                >
                                                    {translate('lab-settings:ADD_TAG')}
                                                </Button>
                                            )}
                                        </Box>
                                    ) : undefined}
                                </Box>
                                {!permissions.isRestrictedExternalUser(currentUser?.role) &&
                                (order.deliveryDetails.price || order.deliveryDetails.price === 0) ? (
                                    <Box flexGrow={0} mr={1}>
                                        <Typography variant="h4">
                                            {formatCurrency(order.deliveryDetails.price, currency)}
                                        </Typography>
                                    </Box>
                                ) : undefined}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <OrderInfoSection
                                order={order}
                                currency={currency}
                                versionCheck={canUseEditFlows}
                                redirectToModifyOrder={redirectToModifyOrder}
                                isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                                isDentist={permissions.isDentist(currentUser?.type)}
                                isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                                isSupplier={isSupplier(currentOrganization._id, order.supplier._id)}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {/* Workflow on Mobile */}
                    {isLabWorkflowEnabled &&
                    !order.isImportedOrder &&
                    order.workFlow?.currentStep &&
                    isSupplier(currentOrganization._id, order.supplier._id) &&
                    order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB &&
                    order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
                    order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED ? (
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <Accordion
                                onChange={handleAccordionChange('WORKFLOW')}
                                expanded={accordionStates['WORKFLOW']}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    data-test="main-order-view.workflow-accordion-mobile"
                                >
                                    <Typography variant="h4">
                                        <Box>{translate('workflow:WORKFLOW')}</Box>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <WorkflowActions order={order} />
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ) : undefined}

                    <Accordion onChange={handleAccordionChange('COMMENTS')} expanded={accordionStates['COMMENTS']}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.comments-accordion"
                        >
                            <Typography variant="h4">{translate('orders:COMMENTS')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <OrderComments
                                order={order}
                                redirectToModifyOrder={redirectToModifyOrder}
                                versionCheck={canUseEditFlows}
                                isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                                isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {isSupplier(currentOrganization._id, order.supplier._id) &&
                    (dentistNote?.note || clinicNote?.note) ? (
                        <Accordion
                            onChange={handleAccordionChange('INTERNAL_NOTES')}
                            expanded={accordionStates['INTERNAL_NOTES']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.dentist-note-accordion"
                            >
                                <Typography variant="h4">
                                    {translate('orders:DENTIST_CLINIC_INTERNAL_NOTES')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {dentistNote?.note ? (
                                    <Box mb={clinicNote?.note ? 2 : 0}>
                                        <Typography variant="h5" gutterBottom>
                                            {translate('orders:DENTIST_NOTES')}
                                        </Typography>
                                        <Typography>{dentistNote.note}</Typography>
                                    </Box>
                                ) : undefined}
                                {clinicNote?.note ? (
                                    <Box>
                                        <Typography variant="h5" gutterBottom>
                                            {translate('orders:CLINIC_NOTES')}
                                        </Typography>
                                        <Typography>{clinicNote.note}</Typography>
                                    </Box>
                                ) : undefined}
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    <Accordion
                        onChange={handleAccordionChange('CONSTRUCTIONS')}
                        expanded={accordionStates['CONSTRUCTIONS']}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.constructions-accordion"
                        >
                            {!order.scannerCase?._id ? (
                                <Typography variant="h4">{translate('orders:CONSTRUCTIONS')}</Typography>
                            ) : (
                                <Typography variant="h4">
                                    {translate(`orders:${order.scannerCase?.source}_CASE`)}
                                </Typography>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <RestorationsSection
                                versionCheck={canUseEditFlows}
                                redirectToModifyOrder={redirectToModifyOrder}
                                isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                                isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                                isSupplier={isSupplier(currentOrganization._id, order.supplier._id)}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        onChange={handleAccordionChange('ATTACHED_SCANS')}
                        expanded={accordionStates['ATTACHED_SCANS']}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.attached-scans-accordion"
                        >
                            <Typography variant="h4">{translate('orders:ATTACHED_SCANS')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <AttachedScansSection
                                order={order}
                                versionCheck={versionCheck}
                                isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                                canShowAttachScannerCaseButton={canShowAttachScannerCaseButton}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        onChange={handleAccordionChange('FILES_AND_IMAGES')}
                        expanded={accordionStates['FILES_AND_IMAGES']}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.files-and-images-accordion"
                        >
                            <Typography variant="h4">{translate('orders:FILES_AND_IMAGES')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FilesImagesSection versionCheck={versionCheck} order={order} />
                        </AccordionDetails>
                    </Accordion>

                    {isSupplier(currentOrganization._id, order.supplier._id) && isOrderFormEnabled ? (
                        <Accordion
                            onChange={handleAccordionChange('ORDER_FORMS')}
                            expanded={accordionStates['ORDER_FORMS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.order-forms-accordion"
                            >
                                <Typography variant="h4">{translate('order-forms:ORDER_FORMS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <OrderFormsSection order={order} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {!permissions.isExternalTechnician(currentUser.role) &&
                    restorationPriceRows(order?.restorations).length ? (
                        <Accordion
                            onChange={handleAccordionChange('PRICE_SPECIFICATION')}
                            expanded={accordionStates['PRICE_SPECIFICATION']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.price-specification-accordion"
                            >
                                <Typography variant="h4">{translate('orders:PRICE_SPECIFICATION')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <PriceSpecificationSectionWithHighlighting order={order} currency={currency} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {!permissions.isRestrictedExternalUser(currentUser?.role) && (
                        <Accordion onChange={handleAccordionChange('DELIVERY')} expanded={accordionStates['DELIVERY']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.delivery-accordion"
                            >
                                <Typography variant="h4">{translate('orders:DELIVERY')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <DeliverySection
                                    versionCheck={canUseEditFlows}
                                    order={order}
                                    redirectToModifyOrder={redirectToModifyOrder}
                                    isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                                    isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Accordion onChange={handleAccordionChange('LAB')} expanded={accordionStates['LAB']}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-test="main-order-view.lab-accordion">
                            <Typography variant="h4">{translate('common:LAB')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <LabSection order={order} />
                        </AccordionDetails>
                    </Accordion>

                    {order?.deliveryDetails?.sample?.type ? (
                        <Accordion
                            onChange={handleAccordionChange('SAMPLE_AND_PACKAGE_CONTENT')}
                            expanded={accordionStates['SAMPLE_AND_PACKAGE_CONTENT']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.sample-and-package-accordion"
                            >
                                <Typography variant="h4">{translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SamplePackageSection
                                    currentUser={currentUser}
                                    order={order}
                                    versionCheck={versionCheck}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {order.rating?.by?._id ? (
                        <Accordion onChange={handleAccordionChange('RATING')} expanded={accordionStates['RATING']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.rating-accordion"
                            >
                                <Typography variant="h4">{translate('orders:RATING')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RateOrderSection rating={order.rating} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {versionCheck &&
                    isSupplier(currentOrganization._id, order.supplier._id) &&
                    !permissions.isExternalTechnician(currentUser?.role) ? (
                        <Accordion
                            onChange={handleAccordionChange('DEVIATIONS')}
                            expanded={accordionStates['DEVIATIONS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.deviations-accordion"
                            >
                                <Typography variant="h4">{translate('deviations:DEVIATIONS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Deviations currentUser={currentUser} order={order} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {versionCheck && isCustomer(currentOrganization._id, order.customer._id) ? (
                        <Accordion
                            onChange={handleAccordionChange('DEVIATIONS')}
                            expanded={accordionStates['DEVIATIONS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.deviations-accordion"
                            >
                                <Typography variant="h4">{translate('deviations:DEVIATIONS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <DeviationForDentist orderId={order._id} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}
                </Grid>

                <Grid item xs={12} md={3}>
                    {!permissions.isExternalTechnician(currentUser.role) ? (
                        <Box mb={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                            <OrderVersionSelect
                                selectedVersion={selectedVersion}
                                order={order}
                                onVersionSelect={handleVersionChange}
                            />
                        </Box>
                    ) : undefined}

                    {isSupplier(currentOrganization._id, order.supplier._id) &&
                    order.supplier._id === (currentUser.organization as Organization)._id &&
                    !permissions.isExternalTechnician(currentUser.role) &&
                    versionCheck ? (
                        <Accordion
                            expanded={accordionStates['INVOICING']}
                            onChange={handleAccordionChange('INVOICING')}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.invoicing-accordion"
                            >
                                <Typography variant="h4">{translate('orders:INVOICING')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <InvoiceActions order={order} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    <Box my={2}>
                        <ChatMessages
                            order={order}
                            isSupplier={isSupplier(currentOrganization._id, order.supplier._id)}
                            isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                            isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                        />
                    </Box>

                    <Accordion
                        expanded={accordionStates['DOCUMENTS_AND_PRINT']}
                        onChange={handleAccordionChange('DOCUMENTS_AND_PRINT')}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.documents-and-print-accordion"
                        >
                            <Typography variant="h4">{translate('orders:DOCUMENTS_AND_PRINT')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <DocumentPrintActions
                                order={order}
                                isExternalTechnician={permissions.isExternalTechnician(currentUser?.role)}
                                isCustomer={isCustomer(currentOrganization._id, order.customer._id)}
                                isSupplier={isSupplier(currentOrganization._id, order.supplier._id)}
                                onHandleDeliveryNoteCreation={onHandleDeliveryNoteCreation}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {isSupplier(currentOrganization._id, order.supplier._id) &&
                    !permissions.isExternalTechnician(currentUser.role) &&
                    !order.isImportedOrder ? (
                        <Accordion
                            expanded={accordionStates['TRANSPORT']}
                            onChange={handleAccordionChange('TRANSPORT')}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.transport-accordion"
                            >
                                <Typography variant="h4">{translate('transport:TRANSPORT')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TransportActions order={order} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    <Accordion onChange={handleAccordionChange('STATUS_LOG')} expanded={accordionStates['STATUS_LOG']}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="main-order-view.status-log-accordion"
                        >
                            <Typography variant="h4">{translate('orders:STATUS_LOG')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <OrderStatusLog
                                orderId={order._id}
                                status={order.status}
                                rejectStatus={order.rejectStatus}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {isLabWorkflowEnabled &&
                    !order.isImportedOrder &&
                    order.workFlow?.currentStep &&
                    isSupplier(currentOrganization._id, order.supplier._id) &&
                    order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB &&
                    order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
                    order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED ? (
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Accordion
                                onChange={handleAccordionChange('WORKFLOW')}
                                expanded={accordionStates['WORKFLOW']}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    data-test="main-order-view.workflow-accordion"
                                >
                                    <Typography variant="h4">
                                        <Box>{translate('workflow:WORKFLOW')}</Box>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <WorkflowActions order={order} />
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ) : undefined}

                    {permissions.isDentist(currentUser?.type) && !order.isImportedOrder ? (
                        <Accordion
                            onChange={handleAccordionChange('MANAGE_PARTICIPANTS')}
                            expanded={accordionStates['MANAGE_PARTICIPANTS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.participants-accordion"
                            >
                                <Typography variant="h4">{translate('orders:PARTICIPANTS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ParticipantsList
                                    linkedDentists={order.linkedDentists || []}
                                    linkedClinics={order.linkedClinics || []}
                                    customEmails={order.customParticipantEmails || []}
                                />
                                <Box mt={2}>
                                    <Button
                                        onClick={handleAddParticipantsClick}
                                        data-test="main-order-view.manage-participants-btn"
                                    >
                                        {translate('orders:MANAGE_PARTICIPANTS')}
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {isExternalLabUsersEnabled &&
                    isSupplier(currentOrganization._id, order.supplier._id) &&
                    !permissions.isExternalTechnician(currentUser?.role) &&
                    !order.isImportedOrder ? (
                        <Accordion
                            onChange={handleAccordionChange('MANAGE_EXTERNAL_USERS')}
                            expanded={accordionStates['MANAGE_EXTERNAL_USERS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.external-users-accordion"
                            >
                                <Typography variant="h4">{translate('orders:MANAGE_EXTERNAL_USERS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ManageExternalTechnicians orderId={order._id} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {order.subcontractSelection ? (
                        <Accordion
                            onChange={handleAccordionChange('SUBCONTRACT_SELECTION')}
                            expanded={accordionStates['SUBCONTRACT_SELECTION'] ?? true}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.subcontract-selection-accordion"
                            >
                                <Typography variant="h4">{translate('orders:SUBCONTRACT_SELECTION')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SubcontractSelection selection={order.subcontractSelection} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}

                    {!permissions.isExternalTechnician(currentUser.role) && !order.isImportedOrder ? (
                        <Accordion onChange={handleAccordionChange('LINKS')} expanded={accordionStates['LINKS']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="main-order-view.links-accordion"
                            >
                                <Typography variant="h4">{translate('orders:LINKS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <OrderLinks order={order} />
                            </AccordionDetails>
                        </Accordion>
                    ) : undefined}
                </Grid>
            </Grid>

            {/* DD-3689: internal notes belong to the lab doing the work, not to the lab that placed the order. */}
            {permissions.isLabTechnician(currentUser.type) &&
            isSupplier(currentOrganization._id, order.supplier._id) ? (
                <StickyInternalNotes order={order} />
            ) : undefined}

            <AssignTagInOrderDialog
                open={isAddEditOpen}
                tags={orderTags}
                rerender={rerender}
                onAddUpdate={handleAddUpdateTag}
                onDialogClose={handleCloseAddEdit}
            />
            <ManageParticipantsDialog
                onClose={handleCloseParticipantsDialog}
                open={participantsDialogOpen}
                order={order}
                orderLinkedDentists={order.linkedDentists}
                orderLinkedClinics={order.linkedClinics}
                orderCustomParticipantEmails={order.customParticipantEmails}
            />
        </Fragment>
    );
};

export default MainOrderView;
